import { StateCreator, create } from 'zustand';

import type {
  TypeContractItem,
  TypePayStatementByEtc,
  TypePayStatementByMonth,
  TypePayStatementByPerDiem,
} from '../../hooks';

const initialState = { date: undefined, startDate: undefined, endDate: undefined, data: null };

export type TypeTabSearchFilter = {
  /** 단일 날짜 */
  date?: string;
  /** 시작 날짜
   * @remarks 기간 조회 시 사용
   */
  startDate?: string;
  /** 종료 날짜
   * @remarks 기간 조회 시 사용
   */
  endDate?: string;
};

export type TypeTabData<T> = TypeTabSearchFilter & {
  /** 조회 결과 */
  data: T | null;
};

type TypeTabInfoStore = TypePayStatementTabSlice &
  TypeContractManagementTabSlice &
  TypeCurrentTabSlice & {
    actions: {
      resetTabInfo: () => void;
    };
  };

type TypePayStatementTabSlice = {
  /** 급여명세서 > 일급 탭*/
  perDiemTab: TypeTabData<TypePayStatementByPerDiem[]>;
  /** 급여명세서 > 추가수당 탭 */
  etcTab: TypeTabData<TypePayStatementByEtc[]>;
  /** 급여명세서 > 월별 탭 */
  monthTab: TypeTabData<TypePayStatementByMonth[]>;
  actions: {
    setPerDiemTab: (data: TypeTabData<TypePayStatementByPerDiem[]>) => void;
    setEtcTab: (data: TypeTabData<TypePayStatementByEtc[]>) => void;
    setMonthTab: (data: TypeTabData<TypePayStatementByMonth[]>) => void;
    resetPayStatement: () => void;
  };
};

/** @description 급여명세서 탭 정보를 가지고 있는 상태 */
const payStatementTabSlice: StateCreator<TypePayStatementTabSlice> = (set) => ({
  perDiemTab: initialState,
  etcTab: initialState,
  monthTab: initialState,
  actions: {
    setPerDiemTab: (perDiemTab) => set(() => ({ perDiemTab })),
    setEtcTab: (etcTab) => set(() => ({ etcTab })),
    setMonthTab: (monthTab) => set(() => ({ monthTab })),
    resetPayStatement: () =>
      set(() => ({
        perDiemTab: initialState,
        etcTab: initialState,
        monthTab: initialState,
      })),
  },
});

type TypeContractManagementTabSlice = {
  /** 계약서 관리 탭 > 계약 취소 탭 */
  canceledTab: TypeTabData<TypeContractItem[]>;
  /** 계약서 관리 탭 > 계약 완료 탭 */
  completedTab: TypeTabData<TypeContractItem[]>;
  actions: {
    setContractManagementCanceled: (data: TypeTabData<TypeContractItem[]>) => void;
    setContractManagementCompleted: (data: TypeTabData<TypeContractItem[]>) => void;
    resetContractManagement: () => void;
  };
};

/** @description 계약서 관리 탭 정보를 가지고 있는 상태 */
const contractManagementTabSlice: StateCreator<TypeContractManagementTabSlice> = (set) => ({
  canceledTab: initialState,
  completedTab: initialState,
  actions: {
    setContractManagementCanceled: (canceledTab) => set(() => ({ canceledTab })),
    setContractManagementCompleted: (completedTab) => set(() => ({ completedTab })),
    resetContractManagement: () =>
      set(() => ({
        canceledTab: initialState,
        completedTab: initialState,
      })),
  },
});

type TypeCurrentTabSlice = {
  /** 현재 선택된 탭 */
  currentTab: string | null;
  actions: {
    setCurrentTab: (tab: string | null) => void;
    resetCurrentTab: () => void;
  };
};

/** @description 현재 탭 정보를 가지고 있는 상태 */
const currentTabSlice: StateCreator<TypeCurrentTabSlice> = (set) => ({
  currentTab: null,
  actions: {
    setCurrentTab: (tab) => set(() => ({ currentTab: tab })),
    resetCurrentTab: () => set(() => ({ currentTab: null })),
  },
});

/** @description 탭 정보를 가지고 있는 상태 */
export const useTabInfoStore = create<TypeTabInfoStore>()((...a) => {
  const currentTab = currentTabSlice(...a);
  const payStatementTab = payStatementTabSlice(...a);
  const contractManagementTab = contractManagementTabSlice(...a);

  return {
    ...currentTab,
    ...payStatementTab,
    ...contractManagementTab,
    actions: {
      ...currentTab.actions,
      ...payStatementTab.actions,
      ...contractManagementTab.actions,
      resetTabInfo: () => {
        currentTab.actions.resetCurrentTab();
        payStatementTab.actions.resetPayStatement();
        contractManagementTab.actions.resetContractManagement();
      },
      resetContractManagement: () => {
        contractManagementTab.actions.resetContractManagement();
        currentTab.actions.resetCurrentTab();
      },
      resetPayStatement: () => {
        payStatementTab.actions.resetPayStatement();
        currentTab.actions.resetCurrentTab();
      },
    },
  };
});
